import { Trophy, RotateCcw, Copy, FileText, FileJson } from 'lucide-react';
import type { WSJFItem } from '../types';

interface ResultsViewProps {
  items: WSJFItem[];
  onReset: () => void;
}

export function ResultsView({ items, onReset }: ResultsViewProps) {
  const sortedItems = [...items].sort((a, b) => {
    if (a.wsjf === null) return 1;
    if (b.wsjf === null) return -1;
    return b.wsjf - a.wsjf;
  });

  const handleExport = async (format: 'markdown' | 'json' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = `# Weighted Smallest Job First (WSJF) Results\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;
        content += `WSJF = Cost of Delay / Job Duration\n\n`;
        content += `| Rank | Item | Cost of Delay | Job Duration | WSJF Score |\n`;
        content += `|------|------|---------------|--------------|------------|\n`;
        sortedItems.forEach((item, index) => {
          content += `| ${index + 1} | ${item.name} | ${item.costOfDelay ?? 'N/A'} | ${item.jobDuration ?? 'N/A'} | ${item.wsjf !== null ? item.wsjf.toFixed(2) : 'N/A'} |\n`;
        });
        filename = `wsjf-results-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = JSON.stringify(sortedItems, null, 2);
        filename = `wsjf-results-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'clipboard':
        content = sortedItems.map((item, index) => 
          `${index + 1}. ${item.name} - WSJF: ${item.wsjf !== null ? item.wsjf.toFixed(2) : 'N/A'} (CoD: ${item.costOfDelay ?? 'N/A'}, Duration: ${item.jobDuration ?? 'N/A'})`
        ).join('\n');
        await navigator.clipboard.writeText(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          WSJF Prioritization Results
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Items ranked by WSJF score: <strong>Cost of Delay ÷ Job Duration</strong>. Higher scores indicate higher priority.
        </p>

        {/* Results Table */}
        <div className="mb-6 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--surface)] border-b-2 border-[var(--rule)]">
                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-sec)]">Rank</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-sec)]">Item</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Cost of Delay</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Job Duration</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">WSJF Score</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, index) => {
                const rank = index + 1;
                const wsjfValue = item.wsjf !== null ? item.wsjf.toFixed(2) : 'N/A';
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-[var(--rule)] hover:bg-[var(--surface)] ${
                      rank === 1 ? 'bg-[var(--mustard-dim)]' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-[var(--text-pri)]">
                      {rank === 1 ? <Trophy className="h-5 w-5 text-[var(--mustard)] inline" /> : rank}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)]">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-center text-[var(--text-sec)]">{item.costOfDelay ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-center text-[var(--text-sec)]">{item.jobDuration ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-center font-bold text-[var(--text-ter)]">{wsjfValue}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Interpretation */}
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <h3 className="font-semibold text-[var(--text-sec)] mb-2">Interpretation</h3>
          <p className="text-sm text-[var(--text-sec)] mb-2">
            <strong>WSJF prioritizes work that delivers the most value in the shortest time.</strong> The top-ranked item ({sortedItems[0]?.name}) has the highest ratio of value (Cost of Delay) to effort (Job Duration).
          </p>
          <p className="text-sm text-[var(--text-sec)]">
            This method helps you focus on high-value, quick wins that move faster through your development pipeline.
          </p>
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}


